/**
 * launchKitApi — Phase 14 Plan 14-09 Task 1.
 *
 * Thin fetch wrappers around Plan 14-08's nine super-admin endpoints under
 * `/api/cms/admin/launch-kits/*`. Auth rides the same `apiRequest` pipeline
 * that the rest of the console uses (Bearer token from localStorage). Error
 * envelope format `{ error, code, details }` mirrors all earlier admin
 * surfaces — surfaced as `LaunchKitApiError` so the page can pattern-match
 * on `err.code` (e.g. `SLUG_CONFLICT`).
 *
 * Pattern mirrors `lib/integrations/discordApi.ts` (Plan 14-03) and
 * `lib/integrations/twitterApi.ts` (Plan 14-05) verbatim — same fetch
 * shape, same envelope decoding. The differences are:
 *   - 9 endpoints rather than 4
 *   - SendOutcome / SendAllResult are Zod-parsed (discriminated union)
 *   - createds POST returns the created LaunchKit row (not 204)
 */

import { apiRequest } from '@/lib/api-client'

import {
  LaunchKitSchema,
  LaunchKitListResponseSchema,
  SendOutcomeSchema,
  SendAllResultSchema,
  SendStatusSchema,
  type LaunchKit,
  type LaunchKitListResponse,
  type LaunchChannel,
  type CreateLaunchKitInput,
  type UpdateLaunchKitInput,
  type SendOutcome,
  type SendAllResult,
  type SendStatus,
  type SetChannelApprovalResponse,
} from './types'

const BASE = '/api/cms/admin/launch-kits'

/**
 * Error class carrying the typed `code` + `details` from the Plan 14-08 error
 * envelope. The page handler can `instanceof LaunchKitApiError` and branch on
 * `err.code` — e.g. show a slug-conflict hint when `SLUG_CONFLICT`.
 */
export class LaunchKitApiError extends Error {
  public readonly code: string
  public readonly status: number
  public readonly details: unknown
  constructor(
    message: string,
    code: string,
    status: number,
    details: unknown = null,
  ) {
    super(message)
    this.name = 'LaunchKitApiError'
    this.code = code
    this.status = status
    this.details = details
    Object.setPrototypeOf(this, LaunchKitApiError.prototype)
  }
}

async function throwFromResponse(response: Response): Promise<never> {
  const data: { error?: string; code?: string; details?: unknown } =
    await response.json().catch(() => ({}))
  const message = data.error || `Request failed (${response.status})`
  const code = data.code || `HTTP_${response.status}`
  throw new LaunchKitApiError(message, code, response.status, data.details)
}

export interface LaunchKitListFilters {
  status?: 'all' | 'draft' | 'published'
  limit?: number
  offset?: number
}

export const launchKitApi = {
  /**
   * GET /api/cms/admin/launch-kits — paginated list. Returns `items` + `total`.
   */
  async list(
    filters: LaunchKitListFilters = {},
  ): Promise<LaunchKitListResponse> {
    const params = new URLSearchParams()
    if (filters.status && filters.status !== 'all') {
      params.set('status', filters.status)
    }
    if (filters.limit != null) params.set('limit', String(filters.limit))
    if (filters.offset != null) params.set('offset', String(filters.offset))
    const qs = params.toString()
    const url = qs ? `${BASE}?${qs}` : BASE
    const res = await apiRequest(url)
    if (!res.ok) await throwFromResponse(res)
    return LaunchKitListResponseSchema.parse(await res.json())
  },

  /**
   * GET /api/cms/admin/launch-kits/:id — single row by id.
   */
  async get(id: string): Promise<LaunchKit> {
    const res = await apiRequest(`${BASE}/${id}`)
    if (!res.ok) await throwFromResponse(res)
    return LaunchKitSchema.parse(await res.json())
  },

  /**
   * POST /api/cms/admin/launch-kits — create. Returns the created row (201).
   * Plan 14-08 service derives `slug` from `title` if omitted (slugify), so
   * callers can omit slug for the "+ New" flow that immediately routes to the
   * created kit's edit page.
   */
  async create(input: CreateLaunchKitInput): Promise<LaunchKit> {
    const res = await apiRequest(BASE, {
      method: 'POST',
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwFromResponse(res)
    return LaunchKitSchema.parse(await res.json())
  },

  /**
   * PUT /api/cms/admin/launch-kits/:id — partial-patch. Each undefined field
   * preserves the existing column server-side. NOTE: `channels` REPLACES the
   * whole JSONB on update — callers must merge any pre-existing channel
   * sub-shapes before submitting (LaunchKitEditPage does the merge).
   */
  async update(id: string, input: UpdateLaunchKitInput): Promise<LaunchKit> {
    const res = await apiRequest(`${BASE}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwFromResponse(res)
    return LaunchKitSchema.parse(await res.json())
  },

  /**
   * DELETE /api/cms/admin/launch-kits/:id — hard-delete (no soft-delete column).
   */
  async delete(id: string): Promise<void> {
    const res = await apiRequest(`${BASE}/${id}`, { method: 'DELETE' })
    if (!res.ok) await throwFromResponse(res)
  },

  /**
   * POST /:id/approve/:channel — toggle the per-channel approve flag.
   * Body `{approved: bool}`. Server returns `{approved: bool}` echoed back
   * (the new value), so optimistic UI flips can verify the round-trip.
   */
  async setChannelApproval(
    id: string,
    channel: LaunchChannel,
    approved: boolean,
  ): Promise<SetChannelApprovalResponse> {
    const res = await apiRequest(`${BASE}/${id}/approve/${channel}`, {
      method: 'POST',
      body: JSON.stringify({ approved }),
    })
    if (!res.ok) await throwFromResponse(res)
    const data = (await res.json()) as { approved: boolean }
    return { approved: !!data.approved }
  },

  /**
   * POST /:id/send/:channel — single-channel send. Returns SendOutcome
   * (sent / skipped / failed). The persisted row is the source of truth;
   * callers should refetch the detail query on success to re-render the
   * SendAllPanel from the row's per-channel sentAt / lastError fields.
   */
  async sendChannel(
    id: string,
    channel: LaunchChannel,
  ): Promise<SendOutcome> {
    const res = await apiRequest(`${BASE}/${id}/send/${channel}`, {
      method: 'POST',
    })
    if (!res.ok) await throwFromResponse(res)
    return SendOutcomeSchema.parse(await res.json())
  },

  /**
   * POST /:id/send-all — sequential 3-channel orchestration. Each channel
   * runs atomically (a failed Twitter does not unwind a successful Discord);
   * the response is informational (toasts) — refetch the detail query after
   * for the row-level state to drive the panel.
   */
  async sendAll(id: string): Promise<SendAllResult> {
    const res = await apiRequest(`${BASE}/${id}/send-all`, {
      method: 'POST',
    })
    if (!res.ok) await throwFromResponse(res)
    return SendAllResultSchema.parse(await res.json())
  },

  /**
   * GET /:id/send-status — flat projection of per-channel approval + sentAt
   * + lastError + launchStatus. Lighter than the full LaunchKit row when the
   * page only needs the orchestration state (not the channel content).
   */
  async getSendStatus(id: string): Promise<SendStatus> {
    const res = await apiRequest(`${BASE}/${id}/send-status`)
    if (!res.ok) await throwFromResponse(res)
    return SendStatusSchema.parse(await res.json())
  },
} as const

export type LaunchKitApi = typeof launchKitApi
